import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, Trash2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchMemoirs,
  createMemoir,
  deleteMemoir,
  selectMemoirs,
  selectMemoirLoading,
  selectMemoirCreating,
  selectMemoirDeleting,
  selectMemoirError,
  clearError
} from '../store/slices/memoirSlice';
import { logout, selectUser } from '../store/slices/authSlice';
import { ThemeToggle } from '../components/ThemeToggle';
import LoadingSpinner from '../components/LoadingSpinner';
import UsageLimits from '../components/UsageLimits';
import apiClient from '../services/api';
import { UserProfile } from '../types/api';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const memoirs = useAppSelector(selectMemoirs);
  const user = useAppSelector(selectUser);
  const isLoading = useAppSelector(selectMemoirLoading);
  const isCreating = useAppSelector(selectMemoirCreating);
  const isDeleting = useAppSelector(selectMemoirDeleting);
  const error = useAppSelector(selectMemoirError);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memoirToDelete, setMemoirToDelete] = useState<any>(null);
  const [newMemoirTitle, setNewMemoirTitle] = useState('');
  const [newMemoirDescription, setNewMemoirDescription] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  useEffect(() => {
    dispatch(fetchMemoirs({ page: 1, size: 20 }));

    // Load user profile and check admin status
    const loadUserData = async () => {
      try {
        setProfileLoading(true);

        // Fetch detailed user profile
        const profile = await apiClient.getDetailedProfile();
        setUserProfile(profile);

        // Check if user is admin based on their role
        const adminStatus = profile.user_role === 'admin' || profile.user_role === 'super_admin';
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error loading user data:', error);
        setIsAdmin(false);
      } finally {
        setProfileLoading(false);
      }
    };

    loadUserData();
  }, [dispatch]);

  const handleCreateMemoir = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      dispatch(clearError());
      const result = await dispatch(createMemoir({
        title: newMemoirTitle,
        description: newMemoirDescription || undefined
      }));

      if (createMemoir.fulfilled.match(result)) {
        setShowCreateModal(false);
        setNewMemoirTitle('');
        setNewMemoirDescription('');
        // Navigate to the new memoir
        navigate(`/memoir/${result.payload.memoir_id}`);
      }
    } catch (error) {
      console.error('Failed to create memoir:', error);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleDeleteClick = (e: React.MouseEvent, memoir: any) => {
    e.preventDefault(); // Prevent navigation
    e.stopPropagation();
    setMemoirToDelete(memoir);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!memoirToDelete) return;

    try {
      await dispatch(deleteMemoir(memoirToDelete.memoir_id));
      setShowDeleteModal(false);
      setMemoirToDelete(null);
    } catch (error) {
      console.error('Failed to delete memoir:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setMemoirToDelete(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return '#2158e8';
      case 'review': return '#2158e8';
      case 'complete': return '#10b981';
      default: return '#606775';
    }
  };

  const { theme } = useTheme();

  const logoSmall = theme === 'dark'
    ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark_small.png`
    : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant_small.png`;

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div>
            <img
              src={logoSmall}
              alt="Debriefr"
              style={{ height: '40px' }}
            />
            {user && <p>Welcome back, {user.full_name || user.username}!</p>}
          </div>
          <div className="header-actions">
            <ThemeToggle />
            {isAdmin && (
              <Link to="/admin" className="btn btn-outline admin-btn">
                ⚙️ Admin
              </Link>
            )}
            <Link to="/settings" className="btn btn-outline">
              Settings
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
              disabled={isCreating}
            >
              {isCreating ? <LoadingSpinner size="sm" color="white" /> : '+ New Book'}
            </button>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="dashboard-main">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        <div className="memoirs-section">
          <div className="section-header">
            <h2>Your Books</h2>
          </div>

          {isLoading ? (
            <div className="loading-container">
              <LoadingSpinner size="lg" text="Loading your memoirs..." />
            </div>
          ) : (
            <div className="memoirs-grid">
              {memoirs?.map(memoir => (
                <div key={memoir.memoir_id} className="memoir-card-wrapper">
                  <Link
                    to={`/memoir/${memoir.memoir_id}`}
                    className="memoir-card"
                  >
                    <div className="memoir-status" style={{ backgroundColor: getStatusColor(memoir.status) }}>
                      {memoir.status}
                    </div>
                    <h3>{memoir.title}</h3>
                    <p className="memoir-description">{memoir.description || 'No description'}</p>
                    <div className="memoir-stats">
                      <div className="stat">
                        <span className="stat-label">Words:</span>
                        <span className="stat-value">{memoir.total_word_count?.toLocaleString() || '0'}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Chapters:</span>
                        <span className="stat-value">{memoir.chapter_count || '0'}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Last edited:</span>
                        <span className="stat-value">{new Date(memoir.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to={`/memoir/${memoir.memoir_id}/settings/publishing`}
                    className="publishing-settings-btn"
                    title="Publishing Settings"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Settings size={18} />
                  </Link>
                  <button
                    className="delete-memoir-btn"
                    onClick={(e) => handleDeleteClick(e, memoir)}
                    title="Delete memoir"
                    disabled={isDeleting}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}

              <div className="memoir-card memoir-card-create" onClick={() => setShowCreateModal(true)}>
                <div className="create-content">
                  <div className="create-icon">+</div>
                  <h3>Create New Book</h3>
                  <p>Start your next life story</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Usage Limits Section - Bottom of page */}
        {!profileLoading && userProfile && (
          <div className="usage-limits-bottom">
            <UsageLimits userProfile={userProfile} compact={true} />
          </div>
        )}
      </main>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Book</h2>
              <button
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateMemoir} className="modal-form">
              <div className="form-group">
                <label htmlFor="title">Title</label>
                <input
                  type="text"
                  id="title"
                  value={newMemoirTitle}
                  onChange={(e) => setNewMemoirTitle(e.target.value)}
                  placeholder="My Life Story"
                  disabled={isCreating}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={newMemoirDescription}
                  onChange={(e) => setNewMemoirDescription(e.target.value)}
                  placeholder="A brief description of your memoir..."
                  rows={3}
                  disabled={isCreating}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isCreating}>
                  {isCreating ? <LoadingSpinner size="sm" color="white" /> : 'Create Book'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && memoirToDelete && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal modal-delete" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Delete Book</h2>
              <button
                className="close-btn"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <p className="delete-warning">
                Are you sure you want to delete <strong>"{memoirToDelete.title}"</strong>?
              </p>
              <p className="delete-warning-note">
                This action cannot be undone. All chapters, notes, and content will be permanently deleted.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? <LoadingSpinner size="sm" color="white" /> : 'Delete Forever'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;